import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaClock,
  FaCopy,
  FaFacebookF,
  FaLinkedinIn,
  FaShareAlt,
} from "react-icons/fa";
import ArticleContent, { extractArticleHeadings } from "../../components/blog/ArticleContent";
import BlogCard, { BlogImage, formatBlogDate } from "../../components/blog/BlogCard";
import usePageMetadata from "../../hooks/usePageMetadata";
import {
  getInsuranceBlogVisitorId,
  hasInsuranceBlogAnalyticsConsent,
} from "../../utils/blogVisitor";
import {
  getInsuranceBlogPost,
  getInsuranceBlogPosts,
  recordInsuranceBlogPostView,
} from "../../services/blogApi";
import "./Blog.css";

const InsuranceBlogPost = () => {
  const { slug } = useParams();
  const articleRef = useRef(null);
  const trackedViewSlugs = useRef(new Set());
  const [reload, setReload] = useState(0);
  const [state, setState] = useState({ status: "loading", post: null, message: "", code: null });
  const [related, setRelated] = useState([]);
  const [progress, setProgress] = useState(0);
  const [shareMessage, setShareMessage] = useState("");
  const [consentVersion, setConsentVersion] = useState(0);

  useEffect(() => {
    const handleConsentChange = () => setConsentVersion((value) => value + 1);
    window.addEventListener("cookieConsentChange", handleConsentChange);
    return () => window.removeEventListener("cookieConsentChange", handleConsentChange);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading", post: null, message: "", code: null });
    window.scrollTo?.({ top: 0, behavior: "auto" });
    getInsuranceBlogPost(slug, controller.signal)
      .then((post) => setState({ status: "ready", post, message: "", code: null }))
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setState({ status: "error", post: null, message: error?.message, code: error?.status });
        }
      });
    return () => controller.abort();
  }, [reload, slug]);

  const post = state.post;

  useEffect(() => {
    const trackedSlug = post?.slug || slug;
    if (
      state.status !== "ready" ||
      !trackedSlug ||
      !hasInsuranceBlogAnalyticsConsent() ||
      trackedViewSlugs.current.has(trackedSlug)
    ) {
      return;
    }

    trackedViewSlugs.current.add(trackedSlug);
    const recordView = async () => {
      try {
        await recordInsuranceBlogPostView(
          trackedSlug,
          getInsuranceBlogVisitorId()
        );
      } catch (error) {
        // Analytics is best-effort and must not disrupt the reading experience.
      }
    };
    recordView();
  }, [consentVersion, post?.slug, slug, state.status]);

  const headings = useMemo(() => extractArticleHeadings(post?.content || ""), [post?.content]);
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/blog/${slug}` : `/blog/${slug}`;
  const metadata = useMemo(
    () => ({
      title: post
        ? `${post.seo_title || post.title} | Thrive Insurance Insights`
        : "Thrive Insurance Insights",
      description: post?.meta_description || post?.excerpt,
      canonical,
      image: post?.cover_image_url,
      type: "article",
      structuredData: post
        ? {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta_description || post.excerpt,
            image: post.cover_image_url || undefined,
            datePublished: post.published_at,
            dateModified: post.updated_at,
            author: { "@type": "Person", name: post.author_name || "Thrive Insurance" },
            publisher: { "@type": "Organization", name: "Thrive Insurance Brokers Limited" },
            mainEntityOfPage: canonical,
          }
        : undefined,
    }),
    [canonical, post]
  );
  usePageMetadata(metadata);

  useEffect(() => {
    if (!post?.category) return undefined;
    const controller = new AbortController();
    getInsuranceBlogPosts({ category: post.category, page_size: 4 }, controller.signal)
      .then((data) => {
        setRelated((data.results || []).filter((item) => item.slug !== post.slug).slice(0, 3));
      })
      .catch(() => setRelated([]));
    return () => controller.abort();
  }, [post?.category, post?.slug]);

  useEffect(() => {
    if (state.status !== "ready") return undefined;
    const updateProgress = () => {
      const article = articleRef.current;
      if (!article) return;
      const start = article.offsetTop;
      const distance = Math.max(article.offsetHeight - window.innerHeight, 1);
      const next = Math.min(100, Math.max(0, ((window.scrollY - start) / distance) * 100));
      setProgress(next);
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [state.status]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareMessage("Link copied");
    } catch (error) {
      setShareMessage("Copy the page address from your browser");
    }
    window.setTimeout(() => setShareMessage(""), 2500);
  };

  const shareArticle = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    copyLink();
  };

  if (state.status === "loading") {
    return (
      <main className="insurance-blog blog-article-page">
        <div className="blog-shell blog-article-loading" aria-label="Loading article">
          <div className="blog-article-loading__line" />
          <div className="blog-article-loading__title" />
          <div className="blog-article-loading__image" />
          <div className="blog-article-loading__body" />
        </div>
      </main>
    );
  }

  if (state.status === "error") {
    const missing = state.code === 404;
    return (
      <main className="insurance-blog blog-article-page">
        <div className="blog-shell blog-state blog-article-state" role={missing ? "status" : "alert"}>
          <span className="blog-state__icon">{missing ? "404" : "!"}</span>
          <h1>{missing ? "This insight is no longer available" : "We could not open this insight"}</h1>
          <p>{missing ? "It may have moved or returned to draft." : state.message || "Please try again."}</p>
          <div>
            {!missing && <button className="blog-button" type="button" onClick={() => setReload((value) => value + 1)}>Try again</button>}
            <Link className="blog-button blog-button--secondary" to="/blog">Browse all insights</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="insurance-blog blog-article-page">
      <div className="blog-reading-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      <article ref={articleRef}>
        <header className="blog-article-header">
          <div className="blog-shell blog-article-header__inner">
            <nav className="blog-breadcrumb" aria-label="Breadcrumb">
              <Link to="/blog"><FaArrowLeft aria-hidden="true" /> Insights</Link>
              <span aria-hidden="true">/</span>
              <Link to={`/blog?category=${encodeURIComponent(post.category)}`}>{post.category}</Link>
            </nav>
            <span className="blog-kicker">{post.category}</span>
            <h1>{post.title}</h1>
            <p className="blog-article-deck">{post.excerpt}</p>
            <div className="blog-article-byline">
              <div className="blog-author-mark" aria-hidden="true">
                {(post.author_name || "Thrive Insurance").charAt(0).toUpperCase()}
              </div>
              <div>
                <strong>{post.author_name || "Thrive Insurance"}</strong>
                <span>
                  <time dateTime={post.published_at}>{formatBlogDate(post.published_at)}</time>
                  <i aria-hidden="true" />
                  <FaClock aria-hidden="true" /> {post.read_time_minutes || 1} min read
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="blog-shell blog-article-cover">
          <BlogImage post={post} eager />
          {post.cover_image_caption && <p>{post.cover_image_caption}</p>}
        </div>

        <div className="blog-shell blog-article-layout">
          <aside className="blog-article-aside">
            {headings.length > 0 && (
              <nav aria-label="On this page">
                <span>In this insight</span>
                <ol>
                  {headings.map((heading) => (
                    <li key={heading.id}><a href={`#${heading.id}`}>{heading.text}</a></li>
                  ))}
                </ol>
              </nav>
            )}
            <div className="blog-share">
              <span>Share this insight</span>
              <div>
                <button type="button" onClick={shareArticle} aria-label="Share article">
                  <FaShareAlt aria-hidden="true" />
                </button>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonical)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                >
                  <FaFacebookF aria-hidden="true" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonical)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on LinkedIn"
                >
                  <FaLinkedinIn aria-hidden="true" />
                </a>
                <button type="button" onClick={copyLink} aria-label="Copy article link">
                  {shareMessage === "Link copied" ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
                </button>
              </div>
              <small aria-live="polite">{shareMessage}</small>
            </div>
          </aside>

          <div className="blog-article-main">
            <ArticleContent content={post.content} />
            {post.tags?.length > 0 && (
              <div className="blog-article-tags" aria-label="Article tags">
                {post.tags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            )}
            <footer className="blog-article-author">
              <div className="blog-author-mark" aria-hidden="true">
                {(post.author_name || "Thrive Insurance").charAt(0).toUpperCase()}
              </div>
              <div>
                <span>Written by</span>
                <strong>{post.author_name || "Thrive Insurance"}</strong>
                <p>Practical guidance from the team helping people and businesses manage risk with confidence.</p>
              </div>
            </footer>
          </div>
        </div>
      </article>

      <section className="blog-article-cta">
        <div className="blog-shell">
          <div>
            <span className="blog-eyebrow blog-eyebrow--light">Turn insight into protection</span>
            <h2>Ready to find cover that fits?</h2>
            <p>Explore our solutions or speak with a Thrive adviser about the risks that matter to you.</p>
          </div>
          <Link className="blog-button blog-button--light" to="/our-products">
            Explore insurance products <FaArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      {related.length > 0 && (
        <section className="blog-related" aria-labelledby="related-stories-title">
          <div className="blog-shell">
            <div className="blog-section-heading">
              <div>
                <span className="blog-eyebrow">Continue exploring</span>
                <h2 id="related-stories-title">More from {post.category}</h2>
              </div>
              <Link className="blog-text-link" to="/blog">
                All insights <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
            <div className="blog-grid">
              {related.map((item) => <BlogCard post={item} key={item.id || item.slug} />)}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default InsuranceBlogPost;
